import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  Button,
  Badge,
  ScrollArea,
} from '@databro/ui';
import { RefreshCw, RotateCcw, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import type { ExecutionBatchStatus, ExecutionStatus } from '@databro/shared';

// Simple progress bar component
function Progress({ value, className }: { value: number; className?: string }) {
  return (
    <div className={`h-2 bg-muted rounded-full overflow-hidden ${className || ''}`}>
      <div
        className="h-full bg-primary transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

interface BatchDetailsProps {
  batchId: string;
  onExecutionSelect?: (executionId: string) => void;
}

const statusColors: Record<ExecutionBatchStatus, string> = {
  running: 'bg-blue-500',
  completed: 'bg-green-500',
  partial: 'bg-yellow-500',
  failed: 'bg-red-500',
};

const statusLabels: Record<ExecutionBatchStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
};

const executionStatusIcons: Record<ExecutionStatus, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-muted-foreground" />,
  queued: <Clock className="h-4 w-4 text-muted-foreground" />,
  running: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
  success: <CheckCircle className="h-4 w-4 text-green-500" />,
  failed: <XCircle className="h-4 w-4 text-red-500" />,
  auth_required: <XCircle className="h-4 w-4 text-yellow-500" />,
  waiting_for_client: <Clock className="h-4 w-4 text-yellow-500" />,
  cancelled: <XCircle className="h-4 w-4 text-muted-foreground" />,
};

export default function BatchDetails({ batchId, onExecutionSelect }: BatchDetailsProps) {
  const queryClient = useQueryClient();

  const { data: batch, isLoading, refetch } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => api.getBatch(batchId),
    refetchInterval: (query) => {
      // Auto-refresh while running
      const data = query.state.data;
      return data?.status === 'running' ? 3000 : false;
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryBatchFailed(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      queryClient.invalidateQueries({ queryKey: ['executions'] });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-2 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Batch not found
      </div>
    );
  }

  const progressPercent = batch.total > 0
    ? Math.round(((batch.completed + batch.failed) / batch.total) * 100)
    : 0;

  const params = batch.parameters as { type: string; startDate: string; endDate: string; timezone: string };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Batch Details</h3>
            <Badge className={statusColors[batch.status as ExecutionBatchStatus]}>
              {statusLabels[batch.status as ExecutionBatchStatus]}
            </Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span className="text-muted-foreground">
              {batch.completed + batch.failed} / {batch.total}
            </span>
          </div>
          <Progress value={progressPercent} />
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span className="text-green-500">{batch.completed} success</span>
            {batch.failed > 0 && (
              <span className="text-red-500">{batch.failed} failed</span>
            )}
            {batch.total - batch.completed - batch.failed > 0 && (
              <span>{batch.total - batch.completed - batch.failed} pending</span>
            )}
          </div>
        </div>

        {/* Parameters */}
        <div className="text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Type</span>
            <span>Date Range</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Range</span>
            <span>{params.startDate} to {params.endDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Timezone</span>
            <span>{params.timezone}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created</span>
            <span>{new Date(batch.createdAt).toLocaleString()}</span>
          </div>
        </div>

        {/* Retry button */}
        {batch.failed > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => retryMutation.mutate()}
            disabled={retryMutation.isPending}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {retryMutation.isPending ? 'Retrying...' : `Retry Failed (${batch.failed})`}
          </Button>
        )}
      </div>

      {/* Executions list */}
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {batch.executions.map((exec) => (
            <button
              key={exec.id}
              className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors flex items-center gap-3"
              onClick={() => onExecutionSelect?.(exec.id)}
            >
              {executionStatusIcons[exec.status as ExecutionStatus]}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {exec.batchLabel}
                </div>
                {exec.error && (
                  <div className="text-xs text-red-500 truncate">
                    {exec.error}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                #{exec.batchIndex + 1}
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
